/**
 * Action: Complaint (start a civil case). Statewide base with the court layer.
 *
 * Every rule cited here comes from the files in data/. Court specifics come from
 * src/courts/ through courtDetail(); anything the files do not cover is listed under
 * "verify" rather than asserted.
 */
import { courtDetail } from "../lib/courtlayer.js";
import {
  CLASS_OPTS, CLASS_TXT, checklist, firstPaperFee, fmt, fmtISO, isLimited, lastDayAfter, lastDayAfterYears, money, opts, parse, todayISO,
} from "../lib/civil.js";

export const id = "complaint";
export const statewide = true;
// The statewide forms its checklist can link, which a case offers when this action is added to it.
export const forms = ["ca-pld-c-001", "ca-pld-c-001-1", "ca-pld-pi-001", "ca-cm-010", "ca-cm-015", "ca-cm-020", "ca-sum-100", "ca-civ-050", "ca-cm-110", "ca-pos-010"];

const KIND_TXT = {
  pi: "Personal injury, property damage or wrongful death",
  contract: "Contract (including a collections case)",
  other: "Another kind of claim",
};
const FORM_COMPLAINT = { pi: "ca-pld-pi-001", contract: "ca-pld-c-001" };
const yesNo = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];

const isCollections = (a) => a.kind === "contract" && isLimited(a.cls) && a.collections === "yes";
const isComplex = (a) => a.cls === "unlimited" && a.complex === "yes";

// What kind of case it is, kept once per case (caseFacts in src/cases.js). A contract claim in a limited case is
// asked whether it is a collections case; any other claim isn't one. A personal injury or contract claim isn't
// an unlawful detainer.
export const caseFacts = {
  from: (f) => ({ cls: f.cls, kind: f.kind, collections: f.collections }),
  to: (a) => ({
    cls: a.cls, limited: a.cls ? (isLimited(a.cls) ? "yes" : "no") : undefined, kind: a.kind,
    ud: a.kind === "pi" || a.kind === "contract" ? "no" : undefined,
    collections: a.kind === "contract" && isLimited(a.cls) ? a.collections : a.kind && a.cls ? "no" : undefined,
  }),
};

/* ------------------------------------------------------------------ steps */
export const steps = [
  {
    id: "case",
    title: "The case",
    intro: "These answers set the filing fee, the forms and the service deadline. The wizard doesn't ask for names or case details.",
    fields: [
      { k: "cls", type: "radio", label: "How is the case classified?", req: true, opts: CLASS_OPTS },
      { k: "kind", type: "radio", label: "What kind of claim is it?", req: true, opts: opts(KIND_TXT) },
      {
        k: "collections", type: "radio", label: "Is it a collections case under CRC 3.740?", req: true,
        show: (a) => a.kind === "contract" && isLimited(a.cls),
        hint: "Money owed in a sum stated to be certain, $35,000 or less (excluding interest and attorney's fees), from a transaction in which property, services or money was acquired on credit. Not a case seeking tort or punitive damages, real or personal property, or a prejudgment writ of attachment (CRC 3.740(a)).",
        opts: yesNo,
      },
      {
        k: "complex", type: "radio", label: "Will you designate the case as complex on the cover sheet?", req: true,
        show: (a) => a.cls === "unlimited",
        hint: "A plaintiff designates a case as complex by filing and serving the CM-010 marked complex with the complaint (CRC 3.401).",
        opts: yesNo,
      },
      {
        k: "pleading", type: "radio", label: "How will the complaint be prepared?", req: true,
        show: (a) => a.kind === "pi" || a.kind === "contract",
        opts: (a) => [
          { v: "form", l: `On the Judicial Council form (${a.kind === "pi" ? "PLD-PI-001" : "PLD-C-001"})` },
          { v: "paper", l: "On pleading paper" },
        ],
      },
    ],
  },
  {
    id: "parties",
    title: "Parties and related cases",
    intro: "These add steps for fictitious defendants and related cases.",
    fields: [
      {
        k: "does", type: "radio", label: "Will the complaint name any defendants by fictitious names (Does)?", req: true,
        opts: yesNo,
      },
      {
        k: "related", type: "radio", label: "Is there a related case, pending or already decided, in a California state or federal court?", req: true,
        hint: "Same parties and similar claims, the same events, the same property, or likely duplication of judicial work if heard by different judges (CRC 3.300(a)).",
        opts: yesNo,
      },
      {
        k: "um", type: "radio", label: "Is it an uninsured motorist case?", req: true,
        show: (a, court) => a.kind === "pi" && courtDetail(court, "rules.uninsuredMotorist", "").known,
        hint: (a, court) => `${court.name} has its own procedure for these.`,
        opts: yesNo,
      },
    ],
  },
  {
    id: "filing",
    title: "Filing",
    intro: (a, court) => `Deadlines are counted on the calendar of ${court.name}.`,
    fields: [
      { k: "fileDate", type: "date", label: "Date you plan to file the complaint", req: true, def: todayISO },
    ],
  },
];

/** Shown on the review step. */
export function summary(a, court) {
  const rows = [];
  const add = (l, v) => v && rows.push([l, v]);
  add("Court", court.name);
  add("Classification", CLASS_TXT[a.cls]);
  add("Claim", KIND_TXT[a.kind]);
  if (a.collections) add("Collections case (CRC 3.740)", a.collections === "yes" ? "Yes" : "No");
  if (a.complex) add("Designated complex", a.complex === "yes" ? "Yes" : "No");
  if (a.pleading) add("Complaint prepared", a.pleading === "form" ? "On the Judicial Council form" : "On pleading paper");
  add("Doe defendants", a.does === "yes" ? "Yes" : a.does === "no" ? "No" : "");
  add("Related case", a.related === "yes" ? "Yes" : a.related === "no" ? "No" : "");
  if (a.um) add("Uninsured motorist case", a.um === "yes" ? "Yes" : "No");
  add("Filing date", fmtISO(a.fileDate));
  return rows;
}

/* ------------------------------------------------------------------ checklist */
export function build(a, court) {
  const c = checklist();
  const fee = firstPaperFee(court, a.cls, { filer: "plaintiff" });
  const collections = isCollections(a);
  const complex = isComplex(a);
  const fileISO = a.fileDate || todayISO();
  const serveDays = collections ? 180 : 60;
  const serveBy = lastDayAfter(court, fileISO, serveDays);
  const outer = lastDayAfterYears(court, fileISO, 3);

  /* ---- prepare ---- */
  const p1 = c.section("prep", "Phase 1. Prepare the papers");
  c.item(p1, "contents", "The complaint states the facts of each cause of action in ordinary and concise language and demands judgment for the relief sought.", "CCP 425.10(a)");
  if (a.kind === "pi") {
    c.item(p1, "noamount", "Do not state the amount of damages demanded: this is a personal injury or wrongful death action.", "CCP 425.10(b)");
  } else {
    c.item(p1, "amount", "State the amount demanded if the complaint seeks money or damages.", "CCP 425.10(a)(2)");
  }
  if (a.pleading === "form") {
    c.item(p1, "form", `Prepare the complaint on Judicial Council form ${a.kind === "pi" ? "PLD-PI-001, with a cause of action attachment for each claim" : "PLD-C-001, with a cause of action attachment for each claim (for breach of contract, PLD-C-001(1))"}.`, "", "", a.kind === "pi" ? ["ca-pld-pi-001"] : ["ca-pld-c-001", "ca-pld-c-001-1"]);
  } else {
    c.item(p1, "format", "Prepare the complaint on pleading paper in the format the rules require: paper, font, line numbering, and the first page layout (attorney information, court, title, case number and nature of the paper).", "CRC 2.100-2.119; 2.111");
  }
  if (a.does === "yes") {
    c.item(p1, "does", "State in the complaint that the plaintiff does not know the true names of the defendants sued by fictitious names.", "CCP 474");
  }
  c.item(p1, "cm010", `Complete the Civil Case Cover Sheet (CM-010) to file with the complaint.${collections ? " Check the box for a collections case under rule 3.740." : ""}${complex ? " Mark it to designate the case as complex." : ""}`, collections ? "CRC 3.220(a)-(b); 3.740(b)" : complex ? "CRC 3.220(a)-(b); 3.401" : "CRC 3.220(a)-(b)", "The clerk must file the paper even without a cover sheet, but leaving it out can bring sanctions (CRC 3.220(c)).", ["ca-cm-010"]);
  const addendum = courtDetail(court, "forms.coverSheetAddendum", "");
  if (!addendum.known || !addendum.value || !addendum.value.classes || addendum.value.classes.includes(a.cls)) {
    c.court(p1, "addendum", court, "forms.coverSheetAddendum", "local rules and forms for a cover sheet or addendum the court requires with a new civil case (CRC 3.220(a) says it is filed in addition to the CM-010)");
  }
  c.item(p1, "summons", "Prepare the summons (SUM-100) for the clerk to issue. One or more summons can be issued for any defendant once the fees are paid.", "CCP 412.10; 412.20", "", ["ca-sum-100"]);
  c.item(p1, "summonsnames", "On the summons, write each party's name exactly as it appears in the caption of the complaint: the same spelling, the same entity form (Inc., LLC) and the same Doe designations.", "CCP 412.20(a)(2)", "The summons must contain the names of the parties to the action.", ["ca-sum-100"]);
  if (a.related === "yes") {
    c.item(p1, "related", "Prepare a Notice of Related Case (CM-015) listing each related case by court, case name, case number and filing date, identifying the earliest-filed case and describing how they are related. Serve and file it no later than 15 days after learning of the related case; as the case is being filed with that knowledge, file it with the complaint.", "CRC 3.300(b)-(f)", "It is filed in every pending related case and served on all parties in each.", ["ca-cm-015"]);
  }

  /* ---- fees ---- */
  const p2 = c.section("fees", "Phase 2. Fees");
  c.item(p2, "first", `First paper filing fee: ${money(fee.amount)} (${CLASS_TXT[a.cls].toLowerCase()}).`, fee.cite, fee.local ? fee.local.text : "The statewide amount. Riverside, San Bernardino and San Francisco add a local surcharge.");
  if (complex) c.item(p2, "complex", "Complex case fee: $1,000, paid with the first paper on behalf of all plaintiffs.", "GC 70616(a); Fee Schedule item 21");

  /* ---- file ---- */
  const p3 = c.section("file", "Phase 3. File and have the summons issued");
  c.court(p3, "efile", court, "efiling.represented", "local rules on whether represented parties must e-file a new civil case (CRC 2.253(b) lets a court require it)",
    { text: "File the complaint, cover sheet and summons with the clerk and pay the fees.", cite: "CCP 412.10" });
  c.item(p3, "issue", "Have the clerk issue the summons. Keep the issued copy: the clerk keeps the original.", "CCP 412.10");
  c.item(p3, "date", `Record the filing date (${fmtISO(fileISO)}). The service deadlines below run from it.`);
  const cmc = c.court(p3, "cmc", court, "rules.cmcNotice", "local rules on how notice of the initial case management conference is given to the plaintiff and who serves it on the defendants",
    { text: "Watch for notice of the initial case management conference. The court must give notice to all parties no later than 45 days before it.", cite: "CRC 3.722(b)" });

  /* ---- serve ---- */
  const p4 = c.section("serve", "Phase 4. What to serve on each defendant");
  c.item(p4, "sc", "A copy of the summons and the complaint.", "CCP 415.10");
  c.court(p4, "adr", court, "rules.adrPackage", "ADR information package and where to get it (CRC 3.221(a) requires the court to make one available when the complaint is filed)",
    { text: "The court's ADR information package. In a general civil case the plaintiff must serve it on each defendant with the complaint.", cite: "CRC 3.221(a), (c)" });
  if (collections || complex) c.item(p4, "cm010copy", `A copy of the Civil Case Cover Sheet (CM-010), because it designates ${collections ? "a collections case" : "a complex case"}.`, collections ? "CRC 3.220(a); 3.740(b)" : "CRC 3.220(a); 3.401", "", ["ca-cm-010"]);
  if (a.kind === "pi") c.item(p4, "civ050", "A Statement of Damages (CIV-050) stating the nature and amount of damages, before any default can be taken. If the defendant hasn't appeared, serve it the same way as a summons.", "CCP 425.11(c)-(d)", "", ["ca-civ-050"]);
  if (a.does === "yes") c.item(p4, "doenotice", "For a defendant served under a fictitious name, the copy of the summons must say on its face that the person is served as (or on behalf of) the person sued under that fictitious name. Without it, no default can be entered against that defendant.", "CCP 474");
  if (cmc.known) c.item(p4, "cmcnotice", "The notice of the case management conference the clerk issued at filing.", cmc.cite);

  /* ---- deadlines ---- */
  const p5 = c.section("dead", "Phase 5. Service deadlines");
  if (collections) {
    c.item(p5, "serve", `Serve every named defendant and file the proofs of service, or get an order for publication of the summons, by ${fmt(serveBy.date)} (180 days after filing).`, "CRC 3.740(d)", "If that doesn't happen, the court may issue an order to show cause about sanctions. Collections cases are exempt from the 60-day rule and from most case management rules unless a defendant files a responsive pleading (CRC 3.740(c), (e)).");
  } else {
    c.item(p5, "serve", `Serve every named defendant and file the proofs of service (POS-010) by ${fmt(serveBy.date)} (60 days after filing).`, "CRC 3.110(b)", "A defendant added by amendment must be served, and proof filed, within 30 days after the amended complaint is filed.", ["ca-pos-010"]);
    c.item(p5, "extend", "If service can't be completed in time, apply for an order extending time before the deadline passes, with a declaration explaining why and the date by which service will be completed.", "CRC 3.110(e)", "", ["ca-cm-020"]);
    c.court(p5, "extendlocal", court, "rules.extensionToServe", "local rules on where an application to extend time to serve is filed and heard", null);
  }
  c.item(p5, "outer", `In any case, the summons and complaint must be served on each defendant within three years after filing (by ${fmt(outer.date)}), and proof of service filed within 60 days after that. Missing it means mandatory dismissal.`, "CCP 583.210; 583.250");
  c.item(p5, "howto", "For each defendant, work out the manner of service and the proof it needs with the Service of summons action.", "CCP 415.10-415.50; 416.10-416.90");

  /* ---- after ---- */
  const p6 = c.section("after", "Phase 6. After filing");
  if (!collections) {
    c.item(p6, "cms", "Calendar the initial case management conference: meet and confer with the other parties no later than 30 calendar days before it, and file and serve a Case Management Statement (CM-110) no later than 15 calendar days before it.", "CRC 3.724; 3.725", "", ["ca-cm-110"]);
    c.item(p6, "jury", "If the plaintiff wants a jury, pay the $150 jury fee on or before the date scheduled for the initial case management conference, unless another plaintiff has paid it.", "CCP 631(b)-(c)");
  } else {
    c.item(p6, "cmsColl", "Case management rules apply to a collections case only once a defendant files a responsive pleading. If one does, calendar the case management conference and file a CM-110 no later than 15 calendar days before it.", "CRC 3.740(c)(2); 3.725", "", ["ca-cm-110"]);
  }
  if (a.um === "yes") c.court(p6, "um", court, "rules.uninsuredMotorist", "local rules for uninsured motorist cases", null);
  if (complex) c.court(p6, "complexapp", court, "rules.complexDesignation", "local rules on how a complex designation is confirmed", null);
  const protem = courtDetail(court, "rules.judgeProTem", "");
  if (protem.known) c.item(p6, "protem", protem.text, protem.cite);
  if (a.does === "yes") c.court(p6, "doeamend", court, "forms.doeAmendment", "local forms and procedure for amending the complaint to name a Doe defendant", null);

  /* ---- rows, verify ---- */
  c.row("Classification", CLASS_TXT[a.cls]);
  c.row("First paper fee", money(fee.amount) + (complex ? " plus $1,000 complex fee" : ""), fee.local ? `Fee schedule of ${court.name}` : "Statewide rate");
  c.row("Serve and file proofs of service by", fmt(serveBy.date), `${serveDays} days after ${fmtISO(fileISO)}${serveBy.moved ? `, moved from ${fmt(serveBy.raw)} to the next court day` : ""}`);
  c.row("Three-year limit for service", fmt(outer.date), "CCP 583.210(a)");
  c.dates(court, parse(fileISO), serveBy.date);
  c.verify("Pleading requirements for particular causes of action, and any statute of limitations, are not in the downloaded files.");
  c.verify("Fee waivers are not covered.");
  c.verify("The fee amounts are from the Statewide Civil Fee Schedule effective January 1, 2026.");

  const out = c.result();
  let n = 1;
  out.sections.forEach((s) => { s.title = s.title.replace(/^Phase \d+\./, `Phase ${n++}.`); });
  return out;
}
